import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isSessionValid } from '../lib/photo-session';
import { debug, error as loggerError } from '../lib/logger';
import { getErrorMessage } from '../lib/errors';

interface CapturedPhoto {
  url: string;
  index: number;
  path: string;
}

export default function CapturePage() {
  const { token } = useParams<{ token: string }>();
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const MAX_PHOTOS = 5;

  // Validate token on mount
  useEffect(() => {
    if (!token) { setIsValid(false); return; }
    isSessionValid(token).then(setIsValid);
  }, [token]);

  // Create single Realtime channel + load existing photos
  useEffect(() => {
    if (!isValid || !token) return;

    const ch = supabase.channel(`photo-session:${token}`);

    ch.subscribe((status) => {
      debug('[capture] Subscribe status', { status });
      if (status === 'SUBSCRIBED') {
        debug('[capture] Channel subscribed');
      }
    });

    channelRef.current = ch;

    // Load existing photos and sync to web (always send sync even if no photos)
    supabase.storage.from('temp-photos').list(token).then(({ data }) => {
      const existing: CapturedPhoto[] = (data || [])
        .filter((f) => f.name.match(/\.(jpg|jpeg)$/i))
        .map((f, i) => {
          const path = `${token}/${f.name}`;
          const { data: urlData } = supabase.storage.from('temp-photos').getPublicUrl(path);
          return { url: urlData.publicUrl, index: i, path };
        });
      setPhotos(existing);

      ch.send({
        type: 'broadcast',
        event: 'session:sync',
        payload: { photos: existing.map((p) => ({ index: p.index, url: p.url })) },
      });
      debug('[capture] Sent session:sync with photos', { count: existing.length });
    });

    return () => { ch.unsubscribe(); };
  }, [isValid, token]);

  const compressImage = useCallback((file: File, maxWidth = 1024, quality = 0.4): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Compression failed'));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const uploadPhoto = useCallback(async (file: File) => {
    if (!token) return;
    if (photos.length >= MAX_PHOTOS) { setError(`Maximum ${MAX_PHOTOS} photos reached`); return; }
    if (file.size > 10 * 1024 * 1024) { setError('File too large (max 10MB)'); return; }

    setIsUploading(true);
    setError(null);
    try {
      const compressed = await compressImage(file);
      const index = photos.length;
      const filename = `${index}-${Date.now()}.jpg`;
      const path = `${token}/${filename}`;

      const { error: uploadError } = await supabase.storage
        .from('temp-photos').upload(path, compressed, { contentType: 'image/jpeg', upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('temp-photos').getPublicUrl(path);
      const url = urlData.publicUrl;

      setPhotos((prev) => [...prev, { url, index, path }]);

      channelRef.current?.send({
        type: 'broadcast', event: 'photo:add', payload: { index, url },
      });
      debug('[capture] Broadcast photo:add', { index, url });
    } catch (err) {
      loggerError('Upload failed', { error: getErrorMessage(err) });
      setError('Upload failed. Tap to retry.');
    } finally {
      setIsUploading(false);
    }
  }, [token, photos.length, compressImage]);

  const removePhoto = useCallback(async (photo: CapturedPhoto) => {
    if (!token) return;
    try {
      await supabase.storage.from('temp-photos').remove([photo.path]);
      setPhotos((prev) => prev.filter((p) => p.index !== photo.index));
      channelRef.current?.send({
        type: 'broadcast', event: 'photo:remove', payload: { index: photo.index },
      });
    } catch (err) { loggerError('Delete failed', { error: getErrorMessage(err) }); }
  }, [token]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadPhoto(file);
    e.target.value = '';
  }, [uploadPhoto]);

  // Loading
  if (isValid === null) {
    return <div className="min-h-screen flex items-center justify-center bg-nb-cream">
      <p className="font-nb text-nb-black/60">Loading...</p>
    </div>;
  }

  // Expired
  if (!isValid) {
    return <div className="min-h-screen flex items-center justify-center p-4 bg-nb-cream">
      <div className="text-center max-w-80">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏰</div>
        <h2 className="font-nb font-bold text-nb-black mb-2 text-xl">Link expired</h2>
        <p className="font-nb text-nb-black/60 text-sm">This link has expired. Ask the operator for a new QR code.</p>
      </div>
    </div>;
  }

  // Done
  if (isDone) {
    return <div className="min-h-screen flex items-center justify-center p-4 bg-nb-cream">
      <div className="text-center max-w-80">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h2 className="font-nb font-bold text-nb-black mb-2 text-xl">Photos sent!</h2>
        <p className="font-nb text-nb-black/60 text-sm">You can close this page now.</p>
      </div>
    </div>;
  }

  const canAddMore = photos.length < MAX_PHOTOS;

  return (
    <div className="min-h-screen p-4 bg-nb-cream">
      <div className="max-w-md mx-auto">
        <div className="flex justify-between items-center mb-6 pb-3 border-b-2 border-nb-black">
          <div>
            <h1 className="font-nb font-bold text-nb-black m-0 text-xl">Add photos for order</h1>
            <p className="font-nb text-nb-black/60 text-xs mt-1">Photos appear on web automatically ✨</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-nb-black flex items-center justify-center text-white text-sm font-bold">📷</div>
        </div>

        {photos.length > 0 && (
          <div className="mb-5">
            <p className="font-nb text-nb-black/60 text-xs mb-2">Photos ({photos.length}/{MAX_PHOTOS})</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {photos.map((photo) => (
                <div key={photo.index} className="relative border-2 border-nb-black overflow-hidden" style={{ borderRadius: 0 }}>
                  <img src={photo.url} alt={`Photo ${photo.index + 1}`} className="w-full aspect-square object-cover" />
                  <button onClick={() => removePhoto(photo)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-nb-red border-2 border-nb-black text-white text-sm flex items-center justify-center cursor-pointer">
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 mb-4 bg-red-100 text-nb-red text-sm text-center font-nb border-2 border-nb-red cursor-pointer"
            onClick={() => { if (error.includes('retry') && fileInputRef.current) fileInputRef.current.click(); }}>
            {error}
          </div>
        )}

        {isUploading && (
          <div className="p-3 mb-4 bg-nb-yellow text-nb-black text-sm text-center font-nb border-2 border-nb-black">
            Uploading...
          </div>
        )}

        <button onClick={() => fileInputRef.current?.click()} disabled={!canAddMore || isUploading}
          className={`font-nb font-bold w-full py-4 border-3 border-nb-black text-white text-base ${canAddMore ? 'bg-nb-black cursor-pointer' : 'bg-nb-black/40 cursor-not-allowed'} mb-3`}
          style={{ borderRadius: 0 }}>
          📷 Take Photo
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ display: 'none' }} />

        <button onClick={() => galleryInputRef.current?.click()} disabled={!canAddMore || isUploading}
          className={`font-nb w-full py-3 border-3 border-nb-black text-sm ${canAddMore ? 'bg-nb-white text-nb-black cursor-pointer' : 'bg-nb-black/20 text-nb-black/40 cursor-not-allowed'} mb-6`}
          style={{ borderRadius: 0 }}>
          🖼️ Choose from Gallery
        </button>
        <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />

        {!canAddMore && (
          <p className="text-center text-nb-black font-nb font-bold text-sm mb-4">
            ✅ Maximum {MAX_PHOTOS} photos captured. Close this page.
          </p>
        )}

        <button onClick={() => setIsDone(true)}
          className="font-nb w-full py-3 border-2 border-nb-black bg-transparent text-nb-black/60 text-sm cursor-pointer"
          style={{ borderRadius: 0 }}>
          Done — close this page
        </button>
      </div>
    </div>
  );
}
